import React, { useEffect, useRef, useState } from "react";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import { useWindowSize } from "../../hooks/useWindowSize";
import Icon from "../Icon";
import spinner from "../../assets/spinnerloader.svg";
import InfoIcon from '../../assets/icons/info.svg';

import {
  PlayerWrapper,
  OptimizationBanner,
  TooltipWrapper,
  TooltipText,
} from "./styles";
import { cancelSubscription } from "../../store/actions/subscription-admin";

type VideoPlayerProps = {
  videoUrl: string;
  isCompressed?: boolean;
  blockDownload?: boolean;
  isOwner?: boolean;
  canBeCompressed?: boolean;
  optimizationRate?: number;
};

const CustomVideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  blockDownload = true,
  isOwner,
  optimizationRate,
  canBeCompressed,
  isCompressed,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<Plyr | null>(null);
  const { isMobile } = useWindowSize();
  const [isVertical, setIsVertical] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isOwner === undefined) return;

    const handleMetadata = () => {
      const { videoWidth, videoHeight } = video;
      const vertical = videoHeight > videoWidth;
      setIsVertical(vertical);

      if (playerRef.current) {
        playerRef.current.destroy();
      }

      const base = ["play", "progress", "current-time", "mute", "fullscreen", "settings"];
      if (!vertical && !isMobile) base.splice(3, 0, "volume");
      if (!blockDownload) base.push("download");

      playerRef.current = new Plyr(video, {
        controls: base,
        settings: ["speed"],
        speed: { selected: 1, options: [0.75, 1, 1.5, 2] },
        i18n: {
          speed: "Velocidad",
          restart: "Reiniciar",
          rewind: "Retroceder",
          play: "Reproducir",
          pause: "Pausar",
          fastForward: "Adelantar",
          seek: "Buscar",
          seekLabel: "{seektime} segundos",
          played: "Reproducido",
          buffered: "En búfer",
          currentTime: "Tiempo actual",
          duration: "Duración",
          volume: "Volumen",
          mute: "Silencio",
          unmute: "Activar sonido",
          enableCaptions: "Activar subtítulos",
          disableCaptions: "Desactivar subtítulos",
          download: "Descargar",
          enterFullscreen: "Pantalla completa",
          exitFullscreen: "Salir de pantalla completa",
          frameTitle: "Reproductor para {title}",
          captions: "Subtítulos",
          settings: "Configuración",
          menuBack: "Atrás",
          normal: "Normal",
          quality: "Calidad",
          loop: "Repetir",
        },
      });
    };

    video.addEventListener("loadedmetadata", handleMetadata);

    return () => {
      video.removeEventListener("loadedmetadata", handleMetadata);
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [videoUrl]);

  return (
    <PlayerWrapper isVertical={isVertical}>
      {isOwner && !!canBeCompressed && (
        <OptimizationBanner>
          {!isCompressed ? (
            <>
              💡 Klouser está optimizando este video <Icon icon={spinner} size="12px" color="white" />
            </>
          ) : (
            `🚀 El peso del video fue optimizado${optimizationRate ? ` un ${optimizationRate}%` : ""}`
          )}
          <TooltipWrapper>
            <Icon icon={InfoIcon} size="14px" color="white" style={{ cursor: "pointer" }} />
            <TooltipText>
              {!isCompressed
                ? "Estamos comprimiendo este video para reducir su peso sin perder calidad. Mientras tanto se puede consumir el video en su tamaño y formato original. Solo tú ves este mensaje."
                : "Klouser optimizó este video para reducir su peso sin perder calidad. Esto mejora muchísimo la experiencia de tus suscriptores. Solo tú ves este mensaje."}
            </TooltipText>
          </TooltipWrapper>
        </OptimizationBanner>
      )}
      <video ref={videoRef} playsInline controls preload="metadata">
        <source src={videoUrl} type="video/mp4" />
      </video>
    </PlayerWrapper>
  );
};

export default React.memo(CustomVideoPlayer);